import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Grow from "@mui/material/Grow";
import { createPortal } from "react-dom";
import { colorTokens } from "../../../styles/designTokens";

const actionSecondaryActive = colorTokens?.actionSecondaryActive || "#e4e4e4";
const actionSecondaryBase2 = colorTokens?.actionSecondaryBase2 || "#f2f2f2";

function isPathActive(currentPath, targetPath) {
  if (!targetPath) return false;
  if (targetPath === "/") return currentPath === "/";
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

function handleHoverEnter(event, active) {
  if (active) return;
  event.currentTarget.style.backgroundColor = actionSecondaryBase2;
}

function handleHoverLeave(event, active) {
  event.currentTarget.style.backgroundColor = active ? actionSecondaryActive : "";
}

export default function CollapsedOverlay({
  overlay,
  activePath,
  onMouseEnter,
  onMouseLeave,
  onItemEnter,
  onItemLeave,
  onItemClick,
}) {
  const ref = useRef(null);
  const [top, setTop] = useState(null);

  useEffect(() => {
    if (!overlay?.open) {
      setTop(null);
      return;
    }
    const raf = requestAnimationFrame(() => {
      if (!ref.current) return;
      const overlayHeight = ref.current.offsetHeight || 0;
      const viewportHeight = window.innerHeight || 0;
      const anchorTop = overlay.anchor?.top ?? 0;
      const minTop = 16 + overlayHeight / 2;
      const maxTop = viewportHeight - 16 - overlayHeight / 2;
      setTop(Math.min(Math.max(anchorTop, minTop), maxTop));
    });
    return () => cancelAnimationFrame(raf);
  }, [overlay?.open, overlay?.anchor?.top]);

  if (!overlay) return null;

  return createPortal(
    <Grow
      in={Boolean(overlay?.open)}
      timeout={{ enter: 180, exit: 120 }}
      mountOnEnter
      unmountOnExit
    >
      <div
        ref={ref}
        key={overlay?.sectionName || "overlay"}
        className="fixed z-[70]"
        style={{
          top: top ?? overlay.anchor?.top ?? 0,
          left: overlay.anchor?.left ?? 0,
          transform: "translateY(-50%)",
          transformOrigin: "top left",
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className="min-w-[230px] max-w-sm rounded-2xl border border-slate-100 bg-white shadow-[0_24px_45px_rgba(15,23,42,0.18)] p-4 flex flex-col gap-2"
          style={{ maxHeight: "calc(100vh - 32px)", overflowY: "auto" }}
        >
          {overlay.items?.map((item, index) => {
            const children = item.subtitle || [];
            const itemActive =
              (item.to && isPathActive(activePath, item.to)) ||
              children.some((s) => s.to && isPathActive(activePath, s.to));
            const ItemComponent = item.to ? Link : "div";
            const itemProps = item.to ? { to: item.to, onClick: onItemClick } : {};

            return (
              <div
                key={`${item.title}-${index}`}
                className="flex flex-col gap-1"
                onMouseEnter={(e) => onItemEnter(e, children)}
                onMouseLeave={() => onItemLeave(children)}
              >
                <ItemComponent
                  {...itemProps}
                  className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${
                    itemActive ? "text-slate-900 font-semibold" : "text-slate-600"
                  }`}
                  style={itemActive ? { backgroundColor: actionSecondaryActive } : undefined}
                  onMouseEnter={(e) => handleHoverEnter(e, itemActive)}
                  onMouseLeave={(e) => handleHoverLeave(e, itemActive)}
                >
                  <span className="truncate">{item.title}</span>
                  {children.length > 0 && (
                    <span className="text-slate-400 text-xs">&rsaquo;</span>
                  )}
                </ItemComponent>
              </div>
            );
          })}
        </div>
      </div>
    </Grow>,
    document.body
  );
}
